import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { FiDollarSign, FiTrendingUp, FiUsers, FiZap, FiActivity, FiAlertCircle } from 'react-icons/fi';

export default function SubscriptionAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await api.get('/admin/credit-analytics');
      setData(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading..." />;
  if (!data) return <p>Failed to load data</p>;

  const { overview, topUsers } = data;

  const statCards = [
    { label: 'Total Revenue', value: `$${(overview.totalRevenue / 100).toFixed(2)}`, icon: <FiDollarSign size={22} />, color: '#22c55e' },
    { label: 'Total Purchases', value: overview.totalPurchases, icon: <FiDollarSign size={22} />, color: '#3b82f6' },
    { label: 'Paid Users', value: overview.paidUsers, icon: <FiUsers size={22} />, color: '#8b5cf6' },
    { label: 'Conversion Rate', value: `${overview.conversionRate}%`, icon: <FiTrendingUp size={22} />, color: '#f59e0b' },
    { label: 'Active Users', value: overview.activeUsers, icon: <FiActivity size={22} />, color: '#06b6d4' },
    { label: 'Need Credits', value: overview.zeroCreditsUsers, icon: <FiAlertCircle size={22} />, color: '#ef4444' }
  ];

  const usagePct = overview.totalCreditsPurchased + (overview.totalUsers * 1000) > 0
    ? Math.round((overview.totalCreditsUsed / (overview.totalCreditsPurchased + overview.totalUsers * 1000)) * 100)
    : 0;

  return (
    <div>
      <h1 style={styles.title}>Credit Analytics</h1>

      <div style={styles.grid}>
        {statCards.map((card, i) => (
          <div key={i} style={styles.card}>
            <div style={{ ...styles.cardIcon, background: card.color + '22', color: card.color }}>
              {card.icon}
            </div>
            <h3 style={styles.cardLabel}>{card.label}</h3>
            <p style={{ ...styles.cardValue, color: card.color }}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Credit Overview */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}><FiZap size={20} /> Credit Overview</h2>
        <div style={styles.overviewGrid}>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Total Credits Purchased</span>
            <span style={styles.overviewValue}>{(overview.totalCreditsPurchased).toLocaleString()}</span>
          </div>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Free Credits Given</span>
            <span style={styles.overviewValue}>{(overview.totalUsers * 1000).toLocaleString()}</span>
          </div>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Total Credits Used</span>
            <span style={styles.overviewValue}>{(overview.totalCreditsUsed).toLocaleString()}</span>
          </div>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Credits Remaining</span>
            <span style={styles.overviewValue}>{(overview.totalCreditsRemaining).toLocaleString()}</span>
          </div>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Avg Credits/User</span>
            <span style={styles.overviewValue}>{(overview.avgCreditsPerUser).toLocaleString()}</span>
          </div>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Total Replies Generated</span>
            <span style={styles.overviewValue}>{(overview.totalReplies).toLocaleString()}</span>
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <div style={styles.barLabel}>
            <span>Usage Rate</span>
            <span style={{ fontWeight: 700 }}>{usagePct}%</span>
          </div>
          <div style={styles.barTrack}>
            <div style={{ ...styles.barFill, width: `${Math.min(usagePct, 100)}%`, background: usagePct > 75 ? '#ef4444' : usagePct > 50 ? '#f59e0b' : '#22c55e' }} />
          </div>
        </div>
      </div>

      {/* Top Users */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}><FiTrendingUp size={20} /> Top Users by Usage</h2>
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span style={{ flex: 2 }}>User</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Credits Used</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Purchased</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Remaining</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Replies</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Last Active</span>
          </div>
          {topUsers.map(u => (
            <div key={u._id} style={styles.tableRow}>
              <span style={{ flex: 2 }}>
                <div style={{ fontWeight: 600 }}>{u.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}</div>
              </span>
              <span style={{ flex: 1, textAlign: 'right', fontWeight: 600, color: '#f59e0b' }}>
                {(u.totalCreditsUsed || 0).toLocaleString()}
              </span>
              <span style={{ flex: 1, textAlign: 'right', color: u.totalCreditsPurchased > 0 ? '#22c55e' : 'var(--text-muted)' }}>
                {(u.totalCreditsPurchased || 0).toLocaleString()}
              </span>
              <span style={{ flex: 1, textAlign: 'right', color: u.credits <= 0 ? '#ef4444' : 'var(--text-secondary)' }}>
                {(u.credits || 0).toLocaleString()}
              </span>
              <span style={{ flex: 1, textAlign: 'right', color: 'var(--text-secondary)' }}>
                {u.totalRepliesGenerated || 0}
              </span>
              <span style={{ flex: 1, textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>
                {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  title: { fontSize: 28, fontWeight: 700, marginBottom: 24, color: 'var(--text-primary)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 },
  card: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, textAlign: 'center' },
  cardIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' },
  cardLabel: { fontSize: 13, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 500 },
  cardValue: { fontSize: 32, fontWeight: 800 },
  section: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 24 },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'var(--text-primary)' },
  overviewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 },
  overviewItem: { background: 'var(--bg-primary)', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 },
  overviewLabel: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },
  overviewValue: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' },
  barLabel: { display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: 'var(--text-secondary)' },
  barTrack: { height: 10, background: 'var(--bg-primary)', borderRadius: 5, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 5, transition: 'width 0.5s' },
  table: { overflowX: 'auto' },
  tableHeader: { display: 'flex', padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 700 },
  tableRow: { display: 'flex', padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', alignItems: 'center', minWidth: 700 }
};
